import { Injectable, NgZone } from '@angular/core';
import { DialogService } from '@salesrun/ngx-dialog';
import { EnvironmentService } from '@shared/environment/environment.service';
import { JwtService } from '@shared/services/jwt.service';
import moment, { Moment } from 'moment';
import { fromEvent, merge, Observable, Subject, timer } from 'rxjs';
import { filter, skipUntil, startWith, switchMap, takeUntil, throttleTime } from 'rxjs/operators';
import { TimeoutPauseService } from './timeout-pause.service';


@Injectable()
export class TimeoutService {
    private _warningVisible = false;
    private _isPause = false;
    private _showWarningAt$: Subject<number> = new Subject();
    private _onLogout: () => void;
    private _destroy$: Observable<boolean>;

    constructor(
        private _jwtService: JwtService,
        private _timeoutPauseService: TimeoutPauseService,
        private _environmentService: EnvironmentService,
        private _ngZone: NgZone,
        private _dialog: DialogService,
    ) {
    }


    setupAutoExpiration(destroy$: Observable<boolean>, onLogout: () => void): void {
        this._onLogout = onLogout;
        this._destroy$ = destroy$;
        this._ngZone.runOutsideAngular(() => this.initSubscriptions());
    }

    private initSubscriptions() {
        merge(
            fromEvent(window, 'click', { passive: true }),
            fromEvent(window, 'keydown', { passive: true }),
            fromEvent(window, 'mousemove', { passive: true }),
        )
            .pipe(
                startWith(null), // initial update
                throttleTime(15000),
                filter(() => !this._warningVisible),
                takeUntil(this._destroy$),
            )
            .subscribe(() => {
                this.updateExpirationDate();
            });

        this._timeoutPauseService.pause$
            .pipe(takeUntil(this._destroy$))
            .subscribe(pause => {
                this._isPause = pause;
            });

        this._showWarningAt$
            .pipe(
                skipUntil(this._environmentService.afterLoad$),
                switchMap(period => timer(period)),
                takeUntil(this._destroy$),
            )
            .subscribe(() => {
                this.onWarningTimeReached();
            });
    }

    private updateExpirationDate(): void {
        this._ngZone.run(() => this._jwtService.refreshToken().subscribe(() => {
            const sessionTimeout = this._jwtService.getSessionTimeout();
            this._showWarningAt$.next(sessionTimeout.warning);
        }));
    }

    private getWarningTime(): Moment {
        const expirationDate = this._jwtService.getExpirationDate();
        const sessionTimeout = this._jwtService.getSessionTimeout();
        const expireOn = moment.unix(expirationDate);
        return expireOn.add(sessionTimeout.warning - sessionTimeout.expiration, 'ms');
    }

    private onWarningTimeReached(): void {
        if (this._isPause) {
            this.updateExpirationDate();
            return;
        }

        const showWarningAt = this.getWarningTime().valueOf();
        const now = moment.utc().valueOf();

        this._warningVisible = showWarningAt <= now;

        if (this._warningVisible) {
            this._ngZone.run(() => this.showWarning());
        }
        else {
            // Looks like token was refreshed in another tab, let's delay the warning
            this._showWarningAt$.next(showWarningAt - now);
        }
    }


    private showWarning(): void {

        const sessionTimeout = this._jwtService.getSessionTimeout();
        const minutesBeforeExpiration = Math.round((sessionTimeout.expiration - sessionTimeout.warning) / 1000 / 60);

        this._dialog.question(`Your SR session will expire in less than ${minutesBeforeExpiration} minutes. Would you like to extend the session?`,
            ['Yes', 'No'])
            .subscribe((answer: string) => {
                if (answer == 'No') {
                    this.onSessionExpiration();
                    return;

                }
                if (!this._jwtService.isTokenExpired()) {
                    this.updateExpirationDate();
                    this._warningVisible = false;
                    return;
                }
                this._dialog
                    .question('Session expired. You will be redirected to login page', ['OK'])
                    .subscribe(() => this.onSessionExpiration());
            });
    }


    private onSessionExpiration(): void {
        this._onLogout();
    }
}